import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Container } from 'src/components/Container';
import { Grid } from 'src/components/Grid';
import { Meta } from 'src/components/Meta';
import { Typography } from 'src/components/Typography';
import { LeaderboardDivision } from 'src/models/leaderboard/_types';
import {
  fetchLeaderboard,
  fetchLeaderboardOverviewResults,
} from 'src/models/leaderboard/leaderboard';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';

import { LeaderboardFilterButton } from './components/LeaderboardFilterButton';
import { LeaderboardSummary } from './components/LeaderboardSummary';
import { useLeaderboardFilters } from './components/useLeaderboardFilters';

export const LeaderboardShowPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tvMode = searchParams.get('tvMode') === 'true';
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.public',
  });

  const { filters } = useLeaderboardFilters();

  const { data: leaderboard, isLoading: isLoadingLb } = useQuery({
    queryKey: ['leaderboard', id],
    queryFn: () => fetchLeaderboard(id!),
    enabled: !!id,
  });

  const { data: results, isLoading: isLoadingResults } = useQuery({
    queryKey: ['leaderboardOverviewResults', id, filters],
    queryFn: () => fetchLeaderboardOverviewResults(id!, filters),
    enabled: !!id,
  });

  const { data: relatedLeaderboard } = useQuery({
    queryKey: ['leaderboard', leaderboard?.displayWithLeaderboardId],
    queryFn: () => fetchLeaderboard(leaderboard!.displayWithLeaderboardId),
    enabled: !!leaderboard?.displayWithLeaderboardId,
  });

  const { data: relatedResults } = useQuery({
    queryKey: [
      'leaderboardOverviewResults',
      leaderboard?.displayWithLeaderboardId,
      filters,
    ],
    queryFn: () =>
      fetchLeaderboardOverviewResults(
        leaderboard!.displayWithLeaderboardId,
        filters
      ),
    enabled: !!leaderboard?.displayWithLeaderboardId,
  });

  if (leaderboard && leaderboard.divisions.length === 0 && !isLoadingResults) {
    navigate(`/leaderboards/public/${id}/all/all`, { replace: true });
    return null;
  }

  const getDivisionResults = (division: LeaderboardDivision) => {
    if (!results?.divisions) return [];
    return (results.divisions[division.key] || []).map((r) => ({
      ...r,
      date: typeof r.date === 'number' ? r.date : r.date,
    }));
  };

  const getRelatedDivisionResults = (division: LeaderboardDivision) => {
    if (!relatedResults?.divisions) return [];
    return relatedResults.divisions[division.key] || [];
  };

  const handleDivisionClick = (
    division: LeaderboardDivision,
    lbId?: string | number
  ) => {
    const targetId = lbId || id;
    navigate(
      `/leaderboards/public/${targetId}/${division.type}/${division.key}`
    );
  };

  const isLoading = isLoadingLb || isLoadingResults;

  return (
    <>
      <Meta title={leaderboard?.name || t('title')} />
      <AppFrameConfig
        id="leaderboard-show"
        containerMaxWidth="lg"
        title={leaderboard?.name || ''}
        backButtonLinkTo="/leaderboards/public"
        backButtonTitle={t('title')}
      />
      <Container>
        {leaderboard?.imageUrl && (
          <Box
            component="img"
            src={leaderboard.imageUrl}
            sx={{ width: '100%', borderRadius: 0 }}
          />
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: relatedLeaderboard ? '50%' : '100%',
                minWidth: {
                  xs: '100%',
                  md: relatedLeaderboard ? '50%' : '100%',
                },
              }}
            >
              <Box sx={{ mx: 2, my: 3 }}>
                <Typography variant="h6">{leaderboard?.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {leaderboard?.description}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {leaderboard?.divisions.map((division, _index) => (
                  <Grid key={division.key} size={{ xs: 12, md: 6 }}>
                    <Box sx={{ px: 1, mb: 3 }}>
                      <LeaderboardSummary
                        division={division}
                        results={getDivisionResults(division)}
                        leaderboard={leaderboard}
                        tvMode={tvMode}
                        onDivisionClick={(div) =>
                          handleDivisionClick(div, leaderboard?.id)
                        }
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {relatedLeaderboard && (
              <Box
                sx={{
                  width: { xs: '100%', md: '50%' },
                }}
              >
                <Box sx={{ mx: 2, my: 3 }}>
                  <Typography variant="h6">
                    {relatedLeaderboard.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {relatedLeaderboard.description}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {relatedLeaderboard.divisions.map((division) => (
                    <Grid key={division.key} size={{ xs: 12, md: 6 }}>
                      <Box sx={{ px: 1, mb: 3 }}>
                        <LeaderboardSummary
                          division={division}
                          results={getRelatedDivisionResults(division)}
                          leaderboard={relatedLeaderboard}
                          tvMode={tvMode}
                          onDivisionClick={(div) =>
                            handleDivisionClick(div, relatedLeaderboard.id)
                          }
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        )}

        {leaderboard && (
          <LeaderboardFilterButton
            availableFilters={leaderboard.filters}
            ageFilters={leaderboard.ageFilters}
          />
        )}
      </Container>
    </>
  );
};
